import {useEffect, useMemo, useState} from 'react'

import {
  parseRouterFormId,
  resolveRoutedEvent,
  type EarliestEnglishSlotInput,
  type ResolvedRoutedEventParams,
} from '@progyny/utils/calcom'

interface UseResolvedCalLinkParamsOptions {
  calLink: string
  routingFormResponses?: Record<string, string>
  language: string
  minDate?: string
  maxDate?: string
  enabled: boolean
}

/**
 * Resolves a "router?form={formId}" calLink into the params the
 * earliest-slot comparison needs. The event type isn't known until the
 * routing form is submitted, so this resolves it asynchronously via
 * progyny_cal.com's /api/router (see resolveRoutedEvent). Every
 * consultation calLink is a routing-form link today - a calLink that
 * isn't resolves to null (no comparison shown).
 */
export function useResolvedCalLinkParams({
  calLink,
  routingFormResponses,
  language,
  minDate,
  maxDate,
  enabled,
}: UseResolvedCalLinkParamsOptions): EarliestEnglishSlotInput | null {
  const formId = parseRouterFormId(calLink)
  const routingFormResponsesKey = routingFormResponses
    ? JSON.stringify(routingFormResponses)
    : undefined

  const [resolvedRoutedEvent, setResolvedRoutedEvent] =
    useState<ResolvedRoutedEventParams | null>(null)

  useEffect(() => {
    setResolvedRoutedEvent(null)
    if (!enabled || !formId) return

    const controller = new AbortController()
    resolveRoutedEvent(formId, routingFormResponses, controller.signal).then(
      setResolvedRoutedEvent
    )
    return () => controller.abort()
    // routingFormResponses is rebuilt on every parent render.
    // using routingFormResponsesKey avoids retriggering the lookup each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, formId, routingFormResponsesKey])

  return useMemo(() => {
    if (!enabled || !formId || !resolvedRoutedEvent) return null

    const dateRange =
      minDate && maxDate ? {startTime: minDate, endTime: maxDate} : {}
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    return {...resolvedRoutedEvent, language, ...dateRange, timeZone}
  }, [enabled, formId, resolvedRoutedEvent, language, minDate, maxDate])
}
